import { usePassportProvider } from "@/context/PassportProvider";
import { useGetLinkedAccounts } from "@/hooks/useGetLinkedAccounts";
import { toSimpleAddressFormat } from "@/utils/util";
import { Body, Divider, Heading, Stack } from "@biom3/react";
import { Fragment, useMemo } from "react";
import { LeaderboardItem } from "./LeaderboardItem";
import type { LeaderboardEntry } from "./LeaderboardProvider";

type LeaderboardProps = {
  entries: LeaderboardEntry[];
  usersEntry?: LeaderboardEntry;
  isLoading: boolean;
  winCondition:
    | {
        pointsFromWinning: number;
        thresholdRank: number;
      }
    | undefined;
  isCreatorLeaderboard?: boolean;
};

const formatEntryName = (
  entry: LeaderboardEntry,
  isCreatorLeaderboard?: boolean,
  userXAccount?: {
    platform: string;
    username: string;
    external_id: string;
  } | null,
) => {
  const xUsername = userXAccount?.username || entry.xUsername;
  const xUserId = userXAccount?.external_id || entry.xUserId;

  if (isCreatorLeaderboard) {
    return xUsername && xUserId ? (
      <a
        href={`https://x.com/i/user/${xUserId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        @{xUsername}
      </a>
    ) : (
      ""
    );
  }

  return toSimpleAddressFormat(entry.walletAddress);
};

export const Leaderboard = ({
  entries,
  usersEntry,
  isLoading,
  winCondition,
  isCreatorLeaderboard,
}: LeaderboardProps) => {
  const { isLoggedIn } = usePassportProvider();
  const userInTopEntries = useMemo(() => {
    return entries.some((entry) => entry.rank === usersEntry?.rank);
  }, [entries, usersEntry]);
  const { data: linkedAccounts } = useGetLinkedAccounts();
  const xAccount = linkedAccounts?.linked_accounts.find(
    (account) => account.platform === "x",
  );

  return (
    <Stack direction="column">
      {entries.map((entry) => (
        <LeaderboardItem
          key={entry.rank}
          position={entry.rank}
          name={formatEntryName(entry, isCreatorLeaderboard)}
          score={entry.balance}
          style={
            entry.rank === usersEntry?.rank
              ? "currentUser"
              : entry.rank === 1
                ? "large"
                : "default"
          }
        />
      ))}
      {!userInTopEntries && isLoggedIn && usersEntry && (
        <Fragment>
          <Divider
            size="xSmall"
            sx={{
              bg: "base.color.translucent.emphasis.100",
              my: "base.spacing.x4",
            }}
          />
          <LeaderboardItem
            key={usersEntry.rank}
            position={usersEntry.rank}
            name={formatEntryName(usersEntry, isCreatorLeaderboard, xAccount)}
            score={usersEntry.balance}
            style="currentUser"
          />
        </Fragment>
      )}
      {winCondition && (
        <Stack gap="0" sx={{ p: "base.spacing.x4" }}>
          <Heading size="xSmall">
            You're {winCondition.pointsFromWinning} points away from winning
          </Heading>
          <Body size="xSmall" sx={{ color: "base.color.text.body.secondary" }}>
            Prize pool will be distributed to top {winCondition.thresholdRank}
          </Body>
        </Stack>
      )}
      {!winCondition && usersEntry && usersEntry.rank > 0 && (
        <Stack gap="0" sx={{ p: "base.spacing.x4" }}>
          <Heading size="xSmall">You're on track to win, keep it up!</Heading>
        </Stack>
      )}
    </Stack>
  );
};
